import React, { useEffect, useMemo, useRef, useState } from 'react'
import { css } from '@emotion/core'
import { shape, number, string, func, bool } from 'prop-types'
import { Image, colors } from '@fs/zion-ui'
import { useSpring, animated } from '@react-spring/web'
import { ANIMATION_TIMING, LAYOUT } from '../../constants/animationConstants'
import { usePWAMode } from '../../hooks/appRootHooks'

// Duration for the fly animation in ms
const FLY_DURATION = ANIMATION_TIMING.ICON_ANIMATION_DURATION + 90
// Maximum time to wait for splash ready signal before fading anyway
const MAX_WAIT_TIME = 2000

const overlayBaseCss = css`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 9999;
`

const backdropBaseCss = css`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${colors.gray00};
`

const ghostCardBaseCss = css`
  position: fixed;
  will-change: transform, opacity;
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`

// State phases
const PHASE = {
  FLYING: 'FLYING',
  FADING: 'FADING',
  COMPLETE: 'COMPLETE',
}

/**
 * SplashTransitionOverlay - Creates a seamless visual transition from a clicked activity card
 * to the splash page icon position using React Spring for reliable animations.
 */
export default function SplashTransitionOverlay({
  transitionId,
  cardRect,
  imageUrl,
  backgroundColor,
  onTransitionComplete,
  targetIconWidth,
  onFlyComplete,
  isSplashMounted,
}) {
  const [phase, setPhase] = useState(PHASE.FLYING)
  const shouldFade = phase === PHASE.FADING || phase === PHASE.COMPLETE
  const isPWA = usePWAMode()
  const flyCompleteHandledRef = useRef(false)
  const [flyComplete, setFlyComplete] = useState(false)

  // Snappier ease-out than quadratic (accelerates quicker, finishes crisp)
  const easingOutCubic = useMemo(() => (t) => 1 - (1 - t) ** 3, [])

  // Calculate target position using estimation
  const targetPosition = useMemo(() => {
    // Prefer `innerWidth` so the center stays stable across route changes where
    // scrollbar presence can change `clientWidth` by a few pixels.
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth
    // In PWA mode, there's no header, so header height is 0
    const headerHeight = isPWA ? 0 : LAYOUT.ESTIMATED_HEADER_HEIGHT
    const targetY = isPWA ? 0 : LAYOUT.TARGET_Y_OFFSET + headerHeight
    const targetWidth = targetIconWidth || 180
    // Calculate left position to match CSS 50% - translateX(-50%)
    // This centers the element perfectly regardless of scrollbar
    const targetX = (viewportWidth - targetWidth) / 2

    return {
      top: targetY,
      left: targetX,
      width: targetWidth,
      height: targetWidth,
    }
  }, [targetIconWidth, isPWA])

  // Initial position from cardRect
  const initialPosition = useMemo(
    () => ({
      top: cardRect?.top ?? 0,
      left: cardRect?.left ?? 0,
      width: cardRect?.width ?? 160,
      height: cardRect?.height ?? 160,
    }),
    [cardRect]
  )

  const transformFromInitialToTarget = useMemo(() => {
    // We render the ghost card at the TARGET rect, and animate a transform that
    // starts at the INITIAL rect (translate + scale) and ends at identity.
    const safeTargetWidth = targetPosition.width || 1
    const safeTargetHeight = targetPosition.height || 1

    const scaleX = (initialPosition.width || 1) / safeTargetWidth
    const scaleY = (initialPosition.height || 1) / safeTargetHeight

    const translateX = (initialPosition.left ?? 0) - (targetPosition.left ?? 0)
    const translateY = (initialPosition.top ?? 0) - (targetPosition.top ?? 0)

    return {
      from: `translate3d(${translateX}px, ${translateY}px, 0) scale(${scaleX}, ${scaleY})`,
      to: 'translate3d(0px, 0px, 0) scale(1, 1)',
    }
  }, [initialPosition, targetPosition])

  // Force fade out after max timeout if splash hangs or something goes wrong
  useEffect(() => {
    const timer = setTimeout(() => {
      if (phase === PHASE.FLYING) {
        setPhase(PHASE.FADING)
      }
    }, MAX_WAIT_TIME)
    return () => clearTimeout(timer)
  }, [phase])

  // Once fly completes, wait for destination splash to mount, then fade overlay
  useEffect(() => {
    if (!flyComplete) return undefined
    if (!isSplashMounted) return undefined
    if (shouldFade) return undefined

    // `isSplashMounted` is signaled by the splash after it has rendered/painted,
    // so we can begin fading immediately.
    setPhase(PHASE.FADING)
    return undefined
  }, [flyComplete, isSplashMounted, shouldFade])

  // Spring animation for the Ghost Card
  const ghostSpring = useSpring({
    from: {
      opacity: 1,
      transform: transformFromInitialToTarget.from,
    },
    to: {
      opacity: shouldFade ? 0 : 1,
      transform: transformFromInitialToTarget.to,
    },
    config: (key) => {
      if (key === 'opacity') return { duration: 350 }
      return { duration: FLY_DURATION, easing: easingOutCubic }
    },
    onRest: (result) => {
      // If we finished the fly animation and should start fading
      if (!shouldFade && result.finished) {
        // Guard: onRest can fire more than once.
        if (flyCompleteHandledRef.current) return
        flyCompleteHandledRef.current = true

        // Make the destination icon visible.
        onFlyComplete?.(transitionId)

        // Mark fly complete; fade starts once destination splash is mounted.
        setFlyComplete(true)
      }
      // If we are fading and it finished, the whole transition is done
      if (shouldFade && result.finished && result.value.opacity === 0) {
        onTransitionComplete?.()
        setPhase(PHASE.COMPLETE)
      }
    },
  })

  // Backdrop animation
  const showBackdrop = phase === PHASE.FLYING
  const [backdropSpring, backdropApi] = useSpring(() => ({
    opacity: 0,
  }))

  // Make sure the backdrop is see-through on first paint, then fades up.
  useEffect(() => {
    if (!showBackdrop) {
      backdropApi.start({ opacity: 0, immediate: true })
      return undefined
    }

    // Kick animation to next frame so initial paint is truly transparent.
    const raf = requestAnimationFrame(() => {
      backdropApi.start({
        opacity: 1,
        immediate: false,
        config: { duration: FLY_DURATION, easing: easingOutCubic },
      })
    })

    return () => cancelAnimationFrame(raf)
  }, [showBackdrop, backdropApi, easingOutCubic])

  const dynamicGhostCss = css`
    ${ghostCardBaseCss};
    top: ${targetPosition.top}px;
    left: ${targetPosition.left}px;
    width: ${targetPosition.width}px;
    height: ${targetPosition.height}px;
    transform-origin: top left;
    background-color: ${backgroundColor || 'transparent'};
  `

  return (
    <div css={overlayBaseCss} data-testid="splash-transition-overlay">
      <animated.div
        css={backdropBaseCss}
        style={{ opacity: backdropSpring.opacity, display: showBackdrop ? 'block' : 'none' }}
      />

      <animated.div style={ghostSpring} css={dynamicGhostCss}>
        {imageUrl && (
          <Image
            src={imageUrl}
            alt=""
            // Image should fill the animated container
            maxWidth="100%"
            maxHeight="100%"
            // Use object-fit like regular images
            style={{ objectFit: 'contain', width: '100%', height: '100%' }}
          />
        )}
      </animated.div>
    </div>
  )
}

SplashTransitionOverlay.propTypes = {
  transitionId: number.isRequired,
  cardRect: shape({
    top: number.isRequired,
    left: number.isRequired,
    width: number.isRequired,
    height: number.isRequired,
  }),
  imageUrl: string,
  backgroundColor: string,
  onTransitionComplete: func,
  targetIconWidth: number,
  onFlyComplete: func,
  isSplashMounted: bool,
}
